import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';

import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { AuthService } from '../../../services/auth.service';
import { SecurityProfileModel } from '../../../loan-state/security-profile';
import { SecurityProfileStore, SecurityProfileQuery } from '../../../loan-state';
import { EMPTY, map, Observable, tap } from 'rxjs';
import { SessionStore } from '../../state';
import { catchError, first, startWith } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { Router } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { RadioButton } from 'primeng/radiobutton';
import { Button } from 'primeng/button';

@Component({
  selector: 'cmg-clear2-ui-switch-profiles',
  templateUrl: './switch-profiles.component.html',
  styleUrls: ['./switch-profiles.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, FormsModule, RadioButton, Button, AsyncPipe],
})
export class SwitchProfilesComponent implements OnInit {
  private readonly _authService = inject(AuthService);
  private readonly _dialogRef = inject(DynamicDialogRef);
  private readonly _securityProfileQuery = inject(SecurityProfileQuery);
  private readonly _securityProfileStore = inject(SecurityProfileStore);
  private readonly _sessionStore = inject(SessionStore);
  private readonly _dialogService = inject(DialogService);
  private readonly _router = inject(Router);

  securityProfiles$ = this._securityProfileQuery.selectAll().pipe(
    tap((securityProfiles) => {
      securityProfiles.sort((a: any, b: any) => {
        if (a.businessChannel === b.businessChannel) {
          let _return = 0;
          if (a.securityProfileName < b.securityProfileName) {
            _return = -1;
          } else if (a.securityProfileName > b.securityProfileName) {
            _return = 1;
          }
          return _return;
        }
        return a.businessChannel > b.businessChannel ? 1 : -1;
      });
    }),
  );
  securityProfileControl = new FormControl<SecurityProfileModel>(null);
  securityProfileInitialValue: SecurityProfileModel = null;
  securityProfileChanged$: Observable<boolean>;

  constructor() {
    this.securityProfileChanged$ = this.securityProfileControl.valueChanges.pipe(
      map((newValue: SecurityProfileModel) => {
        return this.securityProfileInitialValue && newValue && this.securityProfileInitialValue.userOrgProfileId !== newValue.userOrgProfileId;
      }),
      startWith(false),
    );
  }

  ngOnInit(): void {
    const selectedProfile: any = this._securityProfileStore.getValue();
    this.securityProfileControl.setValue(selectedProfile.entities[selectedProfile.active]);
    this.securityProfileInitialValue = selectedProfile.entities[selectedProfile.active];
  }

  selectSecurityProfile(profile?): void {
    const selectedProfile = this.securityProfileControl.value;
    const currentUserOrgProfileId = this._securityProfileStore.getValue()['active'];

    if (selectedProfile.userOrgProfileId === currentUserOrgProfileId) {
      this._dialogRef.close();
      return;
    }

    const saveData = profile || selectedProfile;
    this._securityProfileStore.setActive(saveData.userOrgProfileId);
    this._sessionStore.update({
      userId: saveData.userId,
      userName: saveData.userName,
    });

    this._authService
      .createSecurityProfileAuthToken()
      .pipe(
        tap(() => {
          window.location.href = this.navigationPath;
          this._dialogRef.close();
        }),
        catchError((error) => {
          this._handleError(error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  closeDialog() {
    this._dialogRef.close();
  }

  trackByUserOrgProfileId(index: number, securityProfile: SecurityProfileModel) {
    return securityProfile.userOrgProfileId;
  }

  private _handleError(error: HttpErrorResponse) {
    const messageTitle = error?.error?.Detail ? `<span>${error?.error?.Detail}</span>` : `<span>Your security profile does not have access.</span>`;
    const phoneSpan = '<span><i class="pi pi-phone"></i> <b>(833) 243-4264</b></span>';
    const emailText =
      '<span><i class="pi pi-envelope"></i> <a href="mailto:ClearSupport@cmgfi.com?subject=Requesting access">ClearSupport&#64;cmgfi.com</a></span>';
    const helpUrlSpan =
      '<span><i class="pi pi-desktop"></i> <a href="https://helpdesk.cmgmortgage.com" target="_blank">helpdesk.cmgmortgage.com</a></span>';
    const dialogRef = this._dialogService.open(ConfirmationModalComponent, {
      closable: false,
      width: '500px',
      styleClass: 'confirmation-modal-with-support-links',
      contentStyle: { 'max-height': '500px', overflow: 'auto' },
      data: {
        message: '<div class="d-flex flex-wrap flex-column p-3">' + messageTitle + '<br>' + phoneSpan + emailText + helpUrlSpan + '</div>',
        header: 'Unauthorized Access',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Return to Pipeline',
        rejectLabel: null,
      },
    });

    dialogRef.onClose.pipe(first()).subscribe(() => {
      window.location.href = this.navigationPath;
    });
  }

  get navigationPath() {
    const routeSegments = this._router?.url?.split('/')?.filter((segment) => segment !== '');
    return routeSegments?.includes('dashboard') ? '/dashboard' : '/pipeline';
  }
}
